// What "Add to list" does from a recipe, several recipes or the week plan — the
// pure half of the choices sheet. Ported from the approved prototype (feature
// pack reference/src/merge.ts), with the repo's rules where they differ:
//
// - "Already on your list" is decided by `matchExisting` (quick add's matcher,
//   sure matches only), never by substring: "olive oil" is not "sesame oil",
//   and a false match here becomes a wrong merge.
// - A ticked-off (basket) item counts as on the list. "Use recipe" / "Add both"
//   put it back on the list with the recipe's amount alone — what was bought
//   last time isn't what's needed now.
// - "Not the same" (`separate`) turns a false match into a new item.
//
// Every write is also returned as its inverse, so the toast's Undo can put the
// list back exactly as it was.

import type { Item } from './supabase'
import { displayAmount, matchExisting, mergeAmounts, parseQuantityString } from './recipe'
import { needQuantity, type Need } from './recipeBook'

/** null = undecided (it's on the list); add/skip = a new item; keep/recipe/combine/separate = one on the list. */
export type Choice = null | 'add' | 'skip' | 'keep' | 'recipe' | 'combine' | 'separate'

/** The list item a need collides with, open or in the basket. */
export function matchFor(need: Pick<Need, 'name'>, items: Item[]): Item | null {
  return matchExisting(need.name, items, { includeDone: true })
}

/** New items start ticked (pantry staples unticked); items already on the list start undecided. */
export function initialChoices(needs: Need[], items: Item[]): Choice[] {
  return needs.map(n => (matchFor(n, items) ? null : n.pantry ? 'skip' : 'add'))
}

export interface MergeStatus {
  undecided: number
  adding: number
  changing: number
  unchanged: number
  canApply: boolean
  buttonLabel: string
  /** "4 new, 2 changed, 0 unchanged, 1 still to decide" */
  summary: string
}

export function mergeStatus(choices: Choice[]): MergeStatus {
  const count = (...cs: Choice[]) => choices.filter(c => cs.includes(c)).length
  const undecided = count(null)
  const adding = count('add', 'separate')
  const changing = count('recipe', 'combine')
  const unchanged = count('keep')
  const canApply = undecided === 0 && adding + changing > 0
  const buttonLabel = undecided
    ? `Decide on ${undecided} ${undecided === 1 ? 'item' : 'items'} first`
    : adding + changing ? 'Add to list' : 'Nothing to add'
  const summary = `${adding} new, ${changing} changed, ${unchanged} unchanged` +
    (undecided ? `, ${undecided} still to decide` : '')
  return { undecided, adding, changing, unchanged, canApply, buttonLabel, summary }
}

/**
 * "Add both": the list's amount plus the recipe's. Same unit → summed ("2 cans"
 * + "1 can" = "3 cans"); otherwise written side by side ("1 pack + 400 g").
 */
export function combinedQuantity(existing: string | null, need: Pick<Need, 'parts' | 'quantityText'>, locale?: string): string | null {
  const add = needQuantity(need, locale)
  if (!add) return existing
  if (!existing?.trim()) return add
  const cur = parseQuantityString(existing)
  if (cur && need.quantityText === undefined && need.parts.length === 1) {
    const sum = mergeAmounts(cur, need.parts[0])
    if (sum) return displayAmount(sum.amount, sum.unit, locale)
  }
  return `${existing.trim()} + ${add}`
}

/** What an item's quantity becomes under a choice — the "Stays: … / Becomes: …" preview. */
export function resultQuantity(item: Item, need: Pick<Need, 'parts' | 'quantityText'>, choice: Choice, locale?: string): string | null {
  if (choice === 'recipe') return needQuantity(need, locale) || item.quantity
  if (choice === 'combine') return item.done ? needQuantity(need, locale) || null : combinedQuantity(item.quantity, need, locale)
  return item.quantity
}

export interface ChoicesPlan {
  creates: { id: string; name: string; quantity: string | null; categoryId: string | null; note: string | null }[]
  updates: { id: string; quantity: string | null; note: string | null; restore: boolean }[]
  /** The list as it was, for Undo: rows to soft-delete, and fields to write back. */
  undo: {
    created: string[]
    reverted: { id: string; quantity: string | null; note: string | null; done: boolean }[]
  }
  added: number
  changed: number
}

export interface PlanOptions {
  /** The recipe name(s), written on new items and on changed items without a note. */
  note: string
  newId: () => string
  categoryFor: (name: string) => string | null
  /** The household's categories: a recipe's picked category is used only while it is one of them. */
  categoryIds?: ReadonlySet<string>
  locale?: string
}

/**
 * Turns the decided choices into writes. Several needs landing on the same item
 * are folded into one update, each computed on top of the last (one update per
 * need would each start from the same stale item and the last would win).
 */
export function planChoices(needs: Need[], choices: Choice[], items: Item[], opts: PlanOptions): ChoicesPlan {
  if (choices.some(c => c === null)) throw new Error('Cannot apply while items are undecided')
  const note = opts.note || null
  const creates: ChoicesPlan['creates'] = []
  const updates = new Map<string, ChoicesPlan['updates'][number]>()

  needs.forEach((need, k) => {
    const c = choices[k]
    const quantity = needQuantity(need, opts.locale) || null
    if (c === 'add' || c === 'separate') {
      creates.push({ id: opts.newId(), name: need.name, quantity, categoryId: need.categoryId && opts.categoryIds?.has(need.categoryId) ? need.categoryId : opts.categoryFor(need.name), note })
      return
    }
    if (c !== 'recipe' && c !== 'combine') return
    const item = matchFor(need, items)
    if (!item) return
    const pending = updates.get(item.id)
    const base: Item = pending ? { ...item, quantity: pending.quantity, done: false } : item
    updates.set(item.id, {
      id: item.id,
      quantity: resultQuantity(base, need, c, opts.locale),
      note: item.note || note,
      restore: item.done,
    })
  })

  const byId = new Map(items.map(i => [i.id, i]))
  const changed = [...updates.values()]
  return {
    creates,
    updates: changed,
    undo: {
      created: creates.map(c => c.id),
      reverted: changed.map(u => {
        const i = byId.get(u.id)!
        return { id: i.id, quantity: i.quantity, note: i.note, done: i.done }
      }),
    },
    added: creates.length,
    changed: changed.length,
  }
}
